/* eslint-disable react/no-unused-state */
/* eslint-disable max-classes-per-file, react/no-multi-comp */
// Multiple classes and components used for HOC
/**
 * @typedef {import('../../App/shared/SubscriptionService/SubscriptionFlags').BaseSubscriptionFlags} BaseSubscriptionFlags
 * @typedef {import('./Component').Props} Props
 * @typedef {import('./Component').State} State
 * @typedef {import('./Context').default} Context
 * @typedef {import('./Component').UserTier} UserTier
 */
import {
	withAuthService,
	withSettingService,
	withSubscriptionService,
} from '../../service-container';
import captureError from '../../utils/captureError';
import React from 'react';

const baseSubscriptions = {
	agent: new Map(),
	groups: new Map(),
};

/** @type {Context} */
const defaultContext = {
	features: new Map(),
	groupIsEnabled: () => false,
	isEnabled: () => false,
	setSubscriptionFlags: () => Promise.resolve(),
	subscriptions: {
		...baseSubscriptions,
		homeowner: baseSubscriptions,
	},
	tiers: new Map(),
	userTiers: {},
};

/** @type {React.Context<Context>} */
export const SettingContext = React.createContext(defaultContext);

/** @extends {React.Component<Props, State>} */
export class SettingProvider extends React.Component {
	/** @type {Omit<Props, 'authService' | 'settingService' | 'subscriptionService'>} */
	static defaultProps = {};

	/** @param {Props} props */
	constructor(props) {
		super(props);

		/** @type {State} */
		this.state = {
			...defaultContext,
			groupIsEnabled: this.groupIsEnabled.bind(this),
			isEnabled: this.isEnabled.bind(this),
			setSubscriptionFlags: this.setSubscriptionFlags.bind(this),
		};
	}

	/**
	 * @returns {Promise<void>}
	 */
	async componentDidMount() {
		await this.#setFeatureFlags();
		await this.setSubscriptionFlags();
	}

	/** @param {Props} prevProps */
	async componentDidUpdate(prevProps) {
		if (
			prevProps.authService?.accessToken !==
			this.props.authService?.accessToken
		) {
			await this.setSubscriptionFlags();
		}
	}

	/** @returns {Promise<void>} */
	async #setFeatureFlags() {
		try {
			this.setState({
				/* eslint-disable-next-line react/no-unused-state -- Consumed outside of this component */
				features: await this.props.settingService.getFeatureFlags(),
			});
		} catch (error) {
			captureError(error);
		}
	}

	/** @returns {Promise<void>} */
	async setSubscriptionFlags() {
		if (!this.props.authService?.accessToken) {
			return;
		}

		const { subscriptions, tiers, userTiers } =
			await this.props.subscriptionService.getSubscriptionFlags();

		try {
			this.setState({
				subscriptions,
				tiers,
				userTiers,
			});
		} catch (error) {
			captureError(error);
		}
	}

	/**
	 * @param {UserTier} userTier
	 * @returns {number}
	 */
	getTierValue(userTier) {
		// @ts-ignore -- response keys are snake_case
		const userTiers = this.state.userTiers ?? {};
		const effectiveTierMap = new Map([
			[
				'agent_tier',
				SettingProvider.getMaxTierValue(
					userTiers.agent_tier,
					userTiers.agent_group_tiers
				),
			],
			[
				'manager_tier',
				SettingProvider.getMaxTierValue(
					userTiers.manager_tier,
					userTiers.manager_group_tiers
				),
			],
		]);

		if (effectiveTierMap.has(userTier)) {
			return effectiveTierMap.get(userTier) ?? 0;
		}

		// @ts-ignore -- response keys are snake_case
		return userTiers[userTier] ?? 0;
	}

	/**
	 * @param {number} currentTier
	 * @param {{[key: string]: number}} groupTiers
	 * @returns {number}
	 */
	static getMaxTierValue(currentTier = 0, groupTiers = {}) {
		return Math.max(
			currentTier ?? 0,
			...Object.values(groupTiers).map((value) => value ?? 0)
		);
	}

	/**
	 * @param {string} flag
	 * @param {Omit<UserTier, "agent_group_tiers" | "manager_group_tiers">} userTier
	 * @param {string} [envFlag]
	 * @returns {boolean}
	 */
	isEnabled(flag, userTier, envFlag) {
		const envIsEnabled = this.state.features.get(envFlag ?? flag) ?? false;
		const flagTier = this.state.tiers.get(flag) ?? 0;
		const tier = this.getTierValue(userTier);

		const hasTier = tier >= flagTier;

		return envIsEnabled && hasTier;
	}

	/**
	 * @param {string} flag
	 * @param {string} groupId
	 * @param {'agent' | 'manager'} [type]
	 * @param {string} [envFlag]
	 * @returns {boolean}
	 */
	// eslint-disable-next-line complexity
	groupIsEnabled(flag, groupId, type = 'agent', envFlag = undefined) {
		const envIsEnabled = this.state.features.get(envFlag ?? flag) ?? false;
		const flagTier = this.state.tiers.get(flag) ?? 0;

		const key =
			type === 'agent' ? 'agent_group_tiers' : 'manager_group_tiers';

		// @ts-ignore
		const tier = this.state.userTiers[key][groupId] ?? 0;

		const hasTier = tier >= flagTier;

		return envIsEnabled && hasTier;
	}

	render() {
		return (
			<SettingContext.Provider value={this.state}>
				{this.props.children}
			</SettingContext.Provider>
		);
	}
}

/**
 * @template {Readonly<{ [Key in string]: any}>} PropsTypes
 * @param {React.ComponentType<PropsTypes>} Component
 * @returns {React.ComponentType<Omit<PropsTypes, 'settings'>>}
 */
export function withSettings(Component) {
	/* eslint-disable-next-line react/display-name -- Returned immediately into a named reference */
	return class extends React.Component {
		static contextType = SettingContext;

		render() {
			/* eslint-disable react/jsx-props-no-spreading -- necessary for HOC */
			// @ts-ignore -- TODO: Reconcile React prop issues
			return <Component settings={this.context} {...this.props} />;
			/* eslint-enable react/jsx-props-no-spreading */
		}
	};
}

/** @type {React.ComponentType<Props>} */
export default withAuthService(
	withSettingService(withSubscriptionService(SettingProvider))
);
